import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * CreateFirmDialog — admin-side "New firm" form. Matches the FirmCreate
 * pydantic model on the backend (firms.py:39).
 */
export function CreateFirmDialog({ open, onOpenChange, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", primary_contact_email: "", primary_contact_name: "",
    primary_contact_phone: "", address: "", domains: "",
  });
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Firm name is required");
    setSaving(true);
    try {
      // Match backend FirmCreate model exactly (firms.py:39). Empty optional
      // strings are omitted rather than sent as null so EmailStr validation
      // never trips when the admin leaves an email blank.
      const body = { name: f.name.trim(), domains: [] };
      if (f.primary_contact_name.trim()) body.primary_contact_name = f.primary_contact_name.trim();
      if (f.primary_contact_email.trim()) body.primary_contact_email = f.primary_contact_email.trim();
      if (f.primary_contact_phone.trim()) body.primary_contact_phone = f.primary_contact_phone.trim();
      if (f.address.trim()) body.address = f.address.trim();
      if (f.domains.trim()) {
        body.domains = f.domains
          .split(/[,\s]+/)
          .map((d) => d.trim().toLowerCase().replace(/^@/, ""))
          .filter(Boolean);
      }
      const { data } = await api.post("/operations/firms", body);
      toast.success(`Firm ${data.name || body.name} created`);
      onCreated?.(data);
      onOpenChange(false);
      setF({ name: "", primary_contact_email: "", primary_contact_name: "", primary_contact_phone: "", address: "", domains: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="create-firm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#0A2540]">Create new firm</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Row label="Firm name *">
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="new-firm-name" />
          </Row>
          <Row label="Primary contact name">
            <Input value={f.primary_contact_name} onChange={(e) => set("primary_contact_name", e.target.value)} data-testid="new-firm-contact-name" />
          </Row>
          <Row label="Primary contact email">
            <Input type="email" value={f.primary_contact_email} onChange={(e) => set("primary_contact_email", e.target.value)} data-testid="new-firm-contact-email" />
          </Row>
          <Row label="Phone">
            <Input value={f.primary_contact_phone} onChange={(e) => set("primary_contact_phone", e.target.value)} data-testid="new-firm-phone" />
          </Row>
          <Row label="Address">
            <Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="new-firm-address" />
          </Row>
          <Row label="Email domains" hint="Comma-separated. Applications from any of these auto-attach to this firm.">
            <Input value={f.domains} onChange={(e) => set("domains", e.target.value)} placeholder="e.g. smithlaw.com, smithpartners.com" data-testid="new-firm-domains" />
          </Row>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="new-firm-submit">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create firm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs text-[#374151] mb-1.5 block">{label}</Label>
      {children}
      {hint ? <div className="mt-1 text-[11px] text-[#6B7280]">{hint}</div> : null}
    </div>
  );
}
